import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Initialize authentication state
    const initialize = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        
        if (!mounted) return;

        if (session?.user) {
          setUser(session?.user);
          await loadProfile(session?.user?.id);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initialize();

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (session?.user) {
          setUser(session?.user);
          await loadProfile(session?.user?.id);
        } else {
          setUser(null);
          setProfile(null);
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const loadProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;

      if (data) {
        setProfile({
          id: data?.id,
          email: data?.email,
          fullName: data?.full_name,
          avatarUrl: data?.avatar_url,
          role: data?.role,
          workspaceName: data?.workspace_name,
          timezone: data?.timezone,
          emailNotifications: data?.email_notifications,
          systemNotifications: data?.system_notifications,
          twoFactorEnabled: data?.two_factor_enabled,
          phoneNumber: data?.phone_number,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at
        });
      }
    } catch (error) {
      console.error('Profile load error:', error);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: 'business_owner'
        }
      }
    });

    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
    
    setUser(null);
    setProfile(null);
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');

    const dbUpdates = {};
    if (updates?.fullName !== undefined) dbUpdates.full_name = updates?.fullName;
    if (updates?.avatarUrl !== undefined) dbUpdates.avatar_url = updates?.avatarUrl;
    if (updates?.workspaceName !== undefined) dbUpdates.workspace_name = updates?.workspaceName;
    if (updates?.timezone !== undefined) dbUpdates.timezone = updates?.timezone;
    if (updates?.phoneNumber !== undefined) dbUpdates.phone_number = updates?.phoneNumber;

    const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', user?.id)?.select()?.single();

    if (error) throw error;

    setProfile({
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      avatarUrl: data?.avatar_url,
      role: data?.role,
      workspaceName: data?.workspace_name,
      timezone: data?.timezone,
      emailNotifications: data?.email_notifications,
      systemNotifications: data?.system_notifications,
      twoFactorEnabled: data?.two_factor_enabled,
      phoneNumber: data?.phone_number,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    });

    return data;
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};